import { useEffect } from 'react';

/**
 * Riproduce le interazioni del sito statico (assets/app.js):
 * - reveal-on-scroll (aggiunge la classe "in" agli elementi .reveal)
 * - count-up delle statistiche [data-count]
 * Va richiamato ad ogni cambio pagina (dipendenza sul pathname).
 */
export function usePageEffects(dep?: unknown) {
  useEffect(() => {
    const revs = Array.from(document.querySelectorAll<HTMLElement>('.reveal:not(.in)'));
    let revObserver: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window && revs.length) {
      revObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('in');
              obs.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );
      revs.forEach((el) => revObserver!.observe(el));
    } else {
      revs.forEach((el) => el.classList.add('in'));
    }

    const stats = Array.from(document.querySelectorAll<HTMLElement>('[data-count]'));
    let statObserver: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window && stats.length) {
      statObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const el = e.target as HTMLElement;
            const target = parseFloat(el.getAttribute('data-count') || '0');
            const suffix = el.getAttribute('data-suffix') || '';
            const dur = 1400;
            let start: number | null = null;
            const step = (ts: number) => {
              if (start === null) start = ts;
              const p = Math.min((ts - start) / dur, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              const val = target * eased;
              el.textContent = (target % 1 === 0 ? Math.round(val) : val.toFixed(1)) + suffix;
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            obs.unobserve(el);
          });
        },
        { threshold: 0.5 }
      );
      stats.forEach((el) => statObserver!.observe(el));
    }

    return () => {
      revObserver?.disconnect();
      statObserver?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
}

/** Gestisce lo stato "scrolled" della navbar. */
export function useNavScroll() {
  useEffect(() => {
    const nav = document.querySelector('.nav');
    const onScroll = () => {
      if (!nav) return;
      if (window.scrollY > 40) nav.classList.add('scrolled');
      else nav.classList.remove('scrolled');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
}
